/**
 * Room defaults by type (PRP-WEB-2 W1.4). The sizes are data: `room_defaults` in
 * sketch-policy.json, the medians of 211 founder ESX sketches (census 2026-11-02,
 * DD8). `addRoomOfType` and `addCloset` (when no size is given) read them.
 */
import { POLICY, inToFt } from './policy';
import { axisOf, edgeOf, findRoom, snapIn, tidy } from './geom';
import { makeRoom, nextName, roomOffWall } from './rooms';
import type { Fail, Plan, Pt } from './types';

/** A key of `room_defaults` in sketch-policy.json (e.g. `bedroom`, `reach_in_closet`, `garage_2_car`). */
export type RoomType = string;

export interface RoomDefault { type: RoomType; label: string; name: string; widthFt: number; depthFt: number }

/** Where a new room goes: centred on a plan point, or off wall `vid` of room `rid`, centred `atFt` along it. */
export type RoomPlace = { center: Pt } | { rid: string; vid: number; atFt: number };

export type AddRoomOfTypeResult = { ok: true; rid: string } | Fail<'unknown-type' | 'missing' | 'not-straight'>;

/** The census default for `type`, in feet, or null for an unknown type. */
export function roomDefaults(type: RoomType): RoomDefault | null {
  const row = Object.prototype.hasOwnProperty.call(POLICY.room_defaults, type) ? POLICY.room_defaults[type] : undefined;
  if (!row) return null;
  return { type, label: row.label, name: row.name, widthFt: inToFt(row.width_in), depthFt: inToFt(row.depth_in) };
}

/** Every type in `room_defaults`, in policy order (the Add room menu). */
export function roomTypes(): RoomDefault[] {
  return Object.keys(POLICY.room_defaults).map((t) => roomDefaults(t)!);
}

/**
 * Add a room of `type` at its census size, named by `nextName` (Bedroom 2, Closet 3).
 * Free: centred on `place.center`, width along x. Off a wall: one wall thickness beyond
 * wall `vid` of `rid`, centred at `atFt` along it (clamped to the wall's start), width
 * along the wall and depth outward, sharing the wall (FIELD-2 E6). No door is added.
 * One undoable step under `History.run`.
 */
export function addRoomOfType(plan: Plan, type: RoomType, place: RoomPlace, opts: { id?: string } = {}): AddRoomOfTypeResult {
  const d = roomDefaults(type);
  if (!d) return { ok: false, err: 'unknown-type', message: `Unknown room type: ${type}` };
  const W = d.widthFt, D = d.depthFt;
  const name = nextName(plan, d.name);
  if ('center' in place) {
    const o = { x: snapIn(place.center.x - W / 2), y: snapIn(place.center.y - D / 2) };
    const r = makeRoom(plan, name, [[o.x, o.y], [o.x + W, o.y], [o.x + W, o.y + D], [o.x, o.y + D]], { raw: true, id: opts.id });
    plan.rooms.push(r);
    tidy(plan);
    return { ok: true, rid: r.id };
  }
  const src = findRoom(plan, place.rid);
  const E = src && edgeOf(src, place.vid);
  if (!src || !E) return { ok: false, err: 'missing', message: 'That wall is gone' };
  const t = axisOf(E.a, E.b);
  if (t !== 'h' && t !== 'v') return { ok: false, err: 'not-straight', message: 'Pick a straight wall' };
  const r = roomOffWall(plan, src, place.vid, snapIn(Math.max(0, place.atFt - W / 2)), [[0, 0], [W, 0], [W, D], [0, D]], name, opts.id);
  r.level = src.level;
  plan.rooms.push(r);
  tidy(plan);
  return { ok: true, rid: r.id };
}

